import { Avatar, Tooltip } from '@mantine/core';

import { useAiAssistant } from './AiAssistantProvider';
import classes from '../../components/freud/FreudWidget.module.css';

export interface FreudLauncherProps {
  /** When false, the launcher is not rendered (e.g. client portal users). */
  visible?: boolean;
}

/**
 * Shell-level Freud launcher, shown on every page while the drawer is closed.
 * Resumes the active flow when one exists, prefers a registered treatment-plan
 * context, and otherwise opens the general-purpose AI chat.
 */
export function FreudLauncher({ visible = true }: FreudLauncherProps): JSX.Element | null {
  const {
    open,
    openDrawer,
    startTreatmentPlanRevisionFlow,
    startGuidedTreatmentPlanFlow,
    startProgressNoteGenerateFlow,
    revisionContext,
    guidedContext,
    progressNoteContext,
    flow,
  } = useAiAssistant();

  const geminiDisabled =
    revisionContext?.geminiAiDisabled === true
    || guidedContext?.geminiAiDisabled === true
    || progressNoteContext?.geminiAiDisabled === true;

  if (!visible || open || geminiDisabled) return null;

  const handleClick = () => {
    if (flow) {
      openDrawer(flow);
      return;
    }
    // Prefer progress-note when registered on an appointment page.
    if (progressNoteContext) {
      startProgressNoteGenerateFlow();
      return;
    }
    // Prefer revise when a plan already exists; otherwise open guided create.
    if (revisionContext?.hasExistingPlan) {
      startTreatmentPlanRevisionFlow();
      return;
    }
    if (guidedContext) {
      startGuidedTreatmentPlanFlow(guidedContext);
      return;
    }
    if (revisionContext) {
      startTreatmentPlanRevisionFlow();
      return;
    }
    openDrawer('general-chat');
  };

  const label = 'Open AI assistant';

  return (
    <div className={classes.container} style={{ pointerEvents: 'none' }}>
      <Tooltip label={label} position="left" withArrow>
        <button
          type="button"
          className={classes.fab}
          onClick={handleClick}
          aria-label={label}
          aria-expanded={open}
          style={{ pointerEvents: 'auto' }}
        >
          <Avatar className={classes.fabAvatar} radius="xl" src="/Siggie.png" alt="Dr. Freud" />
        </button>
      </Tooltip>
    </div>
  );
}
